// src/components/LoginForm.tsx
import React, { useState } from "react";
import { AlertModal, PrimaryButton } from "./ui";
import { useAuthContext } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

const LoginForm: React.FC = () => {
  const { login, error, user, loading } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showForgot, setShowForgot] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [alertOpen, setAlertOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await login(form.email, form.password);
  };

  React.useEffect(() => {
    if (user) {
      toast.success("Login successful");
      // Check if there's a redirect path (from where the user was trying to go)
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
    }
  }, [user, navigate, location.state]);

  React.useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  return (
    <>
    <div className="max-w-sm mx-auto bg-white p-6 rounded-2xl shadow space-y-4">
      <h2 className="font-bold text-2xl text-center">Login</h2>
      <p className="text-sm text-gray-600 text-center">Welcome back! Please login to continue.</p>

      <form onSubmit={handleSubmit} className="space-y-3">
        <input
          type="email"
          className="border border-gray-300 px-3 py-2 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-finance-500 focus:border-finance-500 transition-colors"
          placeholder="Email"
          value={form.email}
          onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
          required
        />

        <input
          type="password"
          className="border border-gray-300 px-3 py-2 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-finance-500 focus:border-finance-500 transition-colors"
          placeholder="Password"
          value={form.password}
          onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
          required
        />

        <div className="text-right text-sm">
          <button
            type="button"
            className="text-finance-600 hover:text-finance-700 hover:underline font-medium transition-colors"
            onClick={() => setShowForgot(prev => !prev)}
          >
            Forgot password?
          </button>
        </div>

        {showForgot && (
          <div className="bg-finance-50 border border-finance-200 p-3 rounded-lg animate-fade-in">
            <label className="block text-sm mb-1 text-gray-700">Enter your email to reset password:</label>
            <input
              type="email"
              className="border border-gray-300 px-3 py-2 w-full rounded-lg mb-2 focus:outline-none focus:ring-2 focus:ring-finance-500 focus:border-finance-500 transition-colors"
              placeholder="Recovery email"
              value={recoveryEmail}
              onChange={e => setRecoveryEmail(e.target.value)}
            />
            <PrimaryButton
              type="button"
              onClick={() => setAlertOpen(true)}
              className="text-sm w-full"
            >
              Send Recovery Email
            </PrimaryButton>
          </div>
        )}

        <button
          type="submit"
          className="bg-gradient-to-r from-finance-500 to-finance-600 text-white px-4 py-2 w-full rounded-lg font-semibold shadow-lg shadow-finance-500/30 hover:shadow-xl hover:shadow-finance-500/40 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          disabled={loading}
        >
          {loading ? "Logging in..." : "Login"}
        </button>

        {error && <div className="text-error-600 text-sm text-center">{error}</div>}
      </form>
    </div>
    <AlertModal
      open={alertOpen}
      onClose={() => setAlertOpen(false)}
      message={`Recovery email sent to ${recoveryEmail}`} />
    </>
  );
};

export default LoginForm;
